import { createSlice } from "@reduxjs/toolkit";
import i18n from "../../locales/i18n";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { RootState } from "..";

interface appState {
  sellWithScanner: boolean;
  isDrawerOpen: boolean;
  needNewUpdate: boolean;
  statsRefresh: boolean;
  locale: string;
}

// Get initial locale - check localStorage first on web for reliable value
function getInitialLocale(): string {
  // On web, read from localStorage synchronously
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      const savedLocale = window.localStorage.getItem("appLocale");
      if (savedLocale && ["ar", "fr", "en"].includes(savedLocale)) {
        // Also sync i18n.locale
        i18n.locale = savedLocale;
        return savedLocale;
      }
    } catch (e) {
      // localStorage not available
    }
  }
  return i18n.locale || "ar";
}

const initialState: appState = {
  sellWithScanner: true,
  isDrawerOpen: false,
  needNewUpdate: false,
  statsRefresh: false,
  locale: getInitialLocale(),
};

const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    toggleSellWithScanner: (state) => {
      state.sellWithScanner = !state.sellWithScanner;
    },

    toggleDrawer(state) {
      state.isDrawerOpen = !state.isDrawerOpen;
    },
    setNeedNewUpdate(state) {
      state.needNewUpdate = true;
    },
    toggleStatsRefresh: (state) => {
      state.statsRefresh = !state.statsRefresh;
    },
    setLocale: (state, action) => {
      state.locale = action.payload;
      // Update i18n locale immediately when Redux state changes
      i18n.locale = action.payload;
      // Persist to AsyncStorage
      AsyncStorage.setItem("appLocale", action.payload).catch(() => {
        // silently ignore
      });
      // Also save to localStorage for web sync read
      if (typeof window !== "undefined" && window.localStorage) {
        try {
          window.localStorage.setItem("appLocale", action.payload);
        } catch (e) {
          // Ignore
        }
      }
    },
    // New action to initialize locale from storage
    initializeLocale: (state, action) => {
      state.locale = action.payload;
      i18n.locale = action.payload;
    },
  },
});

// Selectors
export const selectLocale = (state: RootState) => state.app.locale;
export const selectIsRTL = (state: RootState) => state.app.locale === "ar";

const appActions = appSlice.actions;
const appReducer = appSlice.reducer;
export { appActions, appReducer };
